import { QuickstartTheme } from '../types/theme';
import { resolveDeviceFallbackConfig } from '../types/deviceFallbackTheme';
import { IconDropStage } from './IconDrop/IconDropStage';
import { IconDropBrandCluster } from './IconDrop/IconDropBrandCluster';
import { IconDropCopyBlock } from './IconDrop/IconDropCopyBlock';
import { DesktopThankYouStage } from './DeviceFallback/DesktopThankYouStage';
import { iconDropPx } from './IconDrop/iconDropScale';
import { MOBILE_STAGE_WIDTH } from './DeviceFallback/mobileFallbackScale';
import { ThankYouCopy, ThankYouStoreLineMobile, thankYouCopy } from './QuickstartThankYouCopy';
import { useForcePortrait } from '../utils/responsiveHooks';

/**
 * Portrait width for the rotated stage's `--stage-w` scale source. Inside the
 * counter-rotated frame the viewport is landscape, so the phone's short side is
 * `100dvh` — the width the portrait composition was designed for.
 */
const PORTRAIT_STAGE_WIDTH = 'min(100dvh, 480px)';

interface QuickstartThankYouStageProps {
  theme: QuickstartTheme;
  /** Outbound CTA URL rendered as the offer's real anchor href (WebView-safe). */
  ctaHref?: string;
  /** Analytics callback fired on CTA click before the anchor navigates. */
  onCta?: () => void;
  /** True renders the mobile Icon Drop shell; false/undefined renders the desktop split shell. */
  isMobile?: boolean;
  /**
   * Only honored when `isMobile` is true: the vertical (portrait) mobile layout is
   * rendered even when the phone is physically rotated, by counter-rotating the
   * stage when the viewport is landscape (config.FORCE_PORTRAIT_ORIENTATION).
   */
  forcePortrait?: boolean;
}

/**
 * Thank-you / join-failure screen in the Icon Drop UI design system, branched
 * by device.
 *
 * Desktop runs the DeviceFallback split shell (873px artboard, story/panel
 * glass, QR station swapped for the store CTA). Mobile runs the Icon Drop
 * portrait stage (412px artboard, brand cluster upper third, copy block at the
 * foot). Each path spends only its own artboard's tokens — the mobile scales
 * never reach desktop and vice versa.
 *
 * Copy is shared by both paths via `thankYouCopy`; the palette and washes come
 * from the shared `resolveDeviceFallbackConfig`.
 */
export const QuickstartThankYouStage = ({ theme, ctaHref, onCta, isMobile, forcePortrait }: QuickstartThankYouStageProps) => {
  const config = resolveDeviceFallbackConfig(theme, 'none');
  const copy: ThankYouCopy = thankYouCopy(theme);
  // Counter-rotate when a phone is held landscape so the vertical mobile layout
  // always reads upright, regardless of how the device is rotated.
  const { shouldRotate } = useForcePortrait();

  if (!isMobile) {
    return <DesktopThankYouStage theme={theme} config={config} ctaHref={ctaHref} onCta={onCta} copy={copy} />;
  }

  const eyebrow = copy.eyebrow ? (
    <div
      className='whitespace-nowrap text-center'
      style={{
        marginBottom: iconDropPx(4),
        color: '#FFFFFF',
        fontSize: iconDropPx(12),
        fontWeight: 500,
        letterSpacing: '0.08em',
      }}
    >
      {copy.eyebrow}
    </div>
  ) : undefined;

  const stage = (stageWidth: string) => (
    <IconDropStage theme={theme} config={config} stageWidth={stageWidth}>
      <IconDropBrandCluster theme={theme} hideStudio={theme.hideStudioLogoOnCTA} hideGame={theme.hideGameLogoOnCTA} />
      <IconDropCopyBlock
        config={config}
        copy={{
          headline: copy.headline,
          subcopy: copy.subcopy,
          ctaLabel: copy.ctaLabel,
        }}
        metrics={{ headline: 32, cta: 21 }}
        leading={eyebrow}
        subcopyNode={<ThankYouStoreLineMobile gameName={copy.gameName} storePlatform={copy.storePlatform} accentColor={copy.accentColor} />}
        ctaHref={ctaHref}
        onCta={onCta}
      />
      {copy.message ? (
        <p
          className='absolute inset-x-0 z-10 text-center'
          style={{ bottom: iconDropPx(58), color: config.copyColor, fontSize: iconDropPx(12) }}
        >
          {copy.message}
        </p>
      ) : null}
    </IconDropStage>
  );

  if (!forcePortrait || !shouldRotate) {
    return stage(MOBILE_STAGE_WIDTH);
  }

  // Phone held landscape: counter-rotate -90deg into the landscape viewport so
  // the portrait design reads upright. The frame/body widths are the phone's
  // swapped sides; the stage itself gets a PORTRAIT width for `--stage-w`, so
  // text and logos scale exactly as they do when the phone is upright instead
  // of growing with the landscape screen.
  return (
    <div
      className='flex items-center justify-center overflow-hidden'
      style={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        width: '100dvh',
        height: '100dvw',
        transform: 'translate(-50%, -50%) rotate(-90deg)',
        transformOrigin: 'center center',
      }}
    >
      <div style={{ width: PORTRAIT_STAGE_WIDTH, height: '100%' }}>{stage(PORTRAIT_STAGE_WIDTH)}</div>
    </div>
  );
};
